import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { UpdateOrderStatusDialog } from '../../../components/admin/UpdateOrderStatusDialog';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import {
  useDeleteOrderMutation,
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
} from '../../../store/api/ordersApi';
import type { OrderUpdate } from '../../../types/api';
import { formatAmountExact } from '../../../utils/amountFormatter';
import { formatDate, formatRelativeTime } from '../../../utils/dateFormatter';

export default function OrderDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const orderId = id || '';

  const { data, isLoading, error } = useGetOrderByIdQuery(orderId, {
    skip: !orderId,
  });

  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateOrderStatusMutation();
  const [deleteOrder, { isLoading: isDeleting }] = useDeleteOrderMutation();

  // Dialog states
  const [statusDialog, setStatusDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const order = data?.data?.order;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !order) {
    return (
      <div className="text-center py-12">
        <p className="text-danger mb-4">
          {t('admin.orders.details.error.title') || 'Error loading order'}
        </p>
        <p className="text-neutral-medium mb-6">
          {t('admin.orders.details.error.notFound') || 'Order not found'}
        </p>
        <Link
          to="/admin/orders"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200"
        >
          {t('admin.orders.details.back') || 'Back to Orders'}
        </Link>
      </div>
    );
  }

  const handleUpdateStatus = async (
    status: 'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded',
    notes?: string
  ) => {
    try {
      await updateStatus({
        id: orderId,
        data: {
          status,
          notes,
        },
      }).unwrap();

      toast.success(t('admin.orders.updateStatus.success') || 'Order status updated successfully');
      setStatusDialog(false);
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update order status');
    }
  };

  const handleDeleteOrder = async () => {
    try {
      await deleteOrder(orderId).unwrap();

      toast.success(t('admin.orders.deleteSuccess') || 'Order deleted successfully');
      navigate('/admin/orders', { replace: true });
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to delete order');
    }
  };

  const getStatusColor = (orderStatus: string) => {
    switch (orderStatus) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'processing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'refunded':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default:
        return 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50';
    }
  };

  const canDelete = order.status === 'new' || order.status === 'failed' || order.status === 'cancelled';

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <Link
          to="/admin/orders"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline text-sm sm:text-base"
        >
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('admin.orders.details.back') || 'Back to Orders'}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.orders.details.title') || 'Order Details'}
        </h1>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.orders.details.info.orderId') || 'Order ID'}: {order._id.slice(-8)}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Info Card */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Order Information */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4 sm:mb-6">
              {t('admin.orders.details.info.title') || 'Order Information'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.orderId') || 'Order ID'}
                </label>
                <p className="text-neutral-light font-mono text-sm sm:text-base">{order._id}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.user') || 'User'}
                </label>
                <Link
                  to={`/admin/users/${order.userId}`}
                  className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200 hover:underline text-sm sm:text-base"
                >
                  {order.userId}
                </Link>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.provider') || 'Provider'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">{order.providerId}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.section') || 'Section'}
                </label>
                <p className="text-neutral-light capitalize text-sm sm:text-base">{order.section}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.category') || 'Category'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">{order.category}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.service') || 'Service'}
                </label>
                <p className="text-neutral-light font-medium text-sm sm:text-base">{order.service}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.quantity') || 'Quantity'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">{order.quantity}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.price') || 'Price'}
                </label>
                <p className="text-brand-luxury font-semibold text-lg sm:text-xl drop-shadow-[0_0_3px_rgba(230,161,87,0.2)]">
                  ${formatAmountExact(order.finalPrice ?? 0)}
                </p>
              </div>
              {order.providerOrderRef && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.orders.details.info.providerOrderRef') || 'Provider Order Ref'}
                  </label>
                  <p className="text-neutral-light font-mono text-xs sm:text-sm">{order.providerOrderRef}</p>
                </div>
              )}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.created') || 'Created At'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">
                  {formatDate(order.createdAt, 'PP', currentLanguage)}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(order.createdAt, currentLanguage)}
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.orders.details.info.updated') || 'Updated At'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">
                  {formatDate(order.updatedAt, 'PP', currentLanguage)}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(order.updatedAt, currentLanguage)}
                </p>
              </div>
            </div>
          </div>

          {/* Input Fields */}
          {order.inputFields && Object.keys(order.inputFields).length > 0 && (
            <div
              className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
              style={{
                animation: 'slideUp 0.5s ease-out 0.2s both',
              }}
            >
              <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4 sm:mb-6">
                {t('admin.orders.details.inputFields.title') || 'Input Fields'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {Object.entries(order.inputFields).map(([key, value], index) => (
                  <div
                    key={key}
                    style={{
                      animation: `fadeIn 0.3s ease-out ${index * 50}ms both`,
                    }}
                  >
                    <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1 capitalize">
                      {key}
                    </label>
                    <p className="text-neutral-light text-sm sm:text-base break-words">
                      {typeof value === 'string' || typeof value === 'number' ? String(value) : JSON.stringify(value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Updates History */}
          {order.updates && order.updates.length > 0 && (
            <div
              className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
              style={{
                animation: 'slideUp 0.5s ease-out 0.3s both',
              }}
            >
              <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4 sm:mb-6">
                {t('admin.orders.details.updates.title') || 'Update History'}
              </h2>
              <div className="space-y-3">
                {order.updates.map((update: OrderUpdate, index: number) => (
                  <div
                    key={`${update.timestamp}-${index}`}
                    className="p-3 sm:p-4 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50 hover:border-brand-accent/50 transition-all duration-200"
                    style={{
                      animation: `fadeIn 0.3s ease-out ${index * 50}ms both`,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3 sm:gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded border ${getStatusColor(update.status)}`}>
                            {t(`admin.orders.status.${update.status}`) || update.status}
                          </span>
                          <span className="text-xs text-neutral-medium">
                            {formatDate(update.timestamp, 'PP', currentLanguage)}
                          </span>
                        </div>
                        {update.notes && (
                          <p className="text-sm text-neutral-light mt-2">{update.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Status Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.orders.details.info.status') || 'Status'}
            </h2>
            <div className="mb-4">
              <span className={`inline-block px-3 py-2 text-sm font-semibold rounded border ${getStatusColor(order.status)}`}>
                {t(`admin.orders.status.${order.status}`) || order.status}
              </span>
            </div>
          </div>

          {/* Actions Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.3s both',
            }}
          >
            <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.orders.actions.title') || 'Actions'}
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => setStatusDialog(true)}
                className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
              >
                {t('admin.orders.actions.updateStatus') || 'Update Status'}
              </button>
              {canDelete && (
                <button
                  onClick={() => setDeleteDialog(true)}
                  className="w-full px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 font-heading font-semibold rounded-lg hover:bg-red-500/30 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
                >
                  {t('admin.orders.actions.delete') || 'Delete Order'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Update Status Dialog */}
      <UpdateOrderStatusDialog
        isOpen={statusDialog}
        onClose={() => setStatusDialog(false)}
        onConfirm={(status, notes) => {
          void handleUpdateStatus(status, notes);
        }}
        currentStatus={order.status}
        isLoading={isUpdatingStatus}
      />

      {/* Delete Order Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={() => {
          void handleDeleteOrder();
        }}
        title={t('admin.orders.deleteTitle') || 'Delete Order'}
        message={t('admin.orders.deleteConfirm') || 'Are you sure you want to delete this order? This action cannot be undone.'}
        confirmText={t('common.confirm') || 'Confirm'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
